/**
 * Centralised display formatters for ShelfLife.
 *
 * - ``deptLabel`` standardises department names to uppercase for display
 *   only. Underlying values in MongoDB (e.g. ``"Cheese"``) are unchanged.
 * - ``storeAbbr`` returns a 2-letter operational-safety abbreviation
 *   for the user's assigned store, derived from the canonical store
 *   name on the authenticated user object. NEVER infer from cached UI
 *   state — pass ``user.store_name`` directly. Admin/HQ → ``"HQ"``,
 *   unresolved → ``"STORE UNKNOWN"`` (also emits a console warning so
 *   the issue is visible during forensic review).
 */
export const deptLabel = (name) => (name || "").trim().toUpperCase();

// Approved abbreviations for the 12 Uncle Giuseppe's locations
// (operational-safety contract, locked 2026-02). Keys are normalised:
// trimmed, lowercased, whitespace collapsed.
const STORE_ABBR_MAP = {
  "melville":          "MV",
  "smithtown":         "ST",
  "port washington":   "PW",
  "massapequa":        "MS",
  "ramsey":            "RM",
  "tinton falls":      "FT",
  "morris plains":     "MP",
  "yorktown heights":  "YT",
  "levittown":         "LT",
  "bohemia":           "BH",
  "east meadow":       "EM",
  "greenvale":         "GV",
};

const _normaliseStoreName = (s) =>
  (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/**
 * Resolve a store abbreviation for header display.
 *
 *   storeAbbr({ role: "admin" })                            → "HQ"
 *   storeAbbr({ role: "employee", store_name: "Bohemia" })  → "BH"
 *   storeAbbr({ role: "employee", store_name: null })       → "STORE UNKNOWN" + warn
 *   storeAbbr({ role: "employee", store_name: "Bogus" })    → "STORE UNKNOWN" + warn
 *
 * Callers MUST pass the authenticated ``user`` object verbatim. Do not
 * derive the store from a localStorage cache, URL query, or any other
 * mutable client state — that would defeat the operational-safety
 * intent of this label.
 */
export function storeAbbr(user) {
  if (!user || typeof user !== "object") {
    if (typeof console !== "undefined") {
      console.warn("[storeAbbr] called with no user; falling back to STORE UNKNOWN");
    }
    return "STORE UNKNOWN";
  }
  const role = (user.role || "").toLowerCase();
  // Admin / HQ-tier roles are intentionally not tied to a store. We
  // surface a stable "HQ" label so operators can confirm they're not
  // accidentally logged in as an employee.
  if (role === "admin") return "HQ";
  const key = _normaliseStoreName(user.store_name);
  if (!key) {
    if (typeof console !== "undefined") {
      console.warn(
        "[storeAbbr] no store_name on user; rendering STORE UNKNOWN " +
        `(user_id=${user.id || "?"} role=${role || "?"} store_id=${user.store_id || "?"})`,
      );
    }
    return "STORE UNKNOWN";
  }
  const abbr = STORE_ABBR_MAP[key];
  if (!abbr) {
    if (typeof console !== "undefined") {
      console.warn(
        `[storeAbbr] unrecognised store_name=${JSON.stringify(user.store_name)} ` +
        `(user_id=${user.id || "?"} role=${role || "?"} store_id=${user.store_id || "?"}); ` +
        "rendering STORE UNKNOWN",
      );
    }
    return "STORE UNKNOWN";
  }
  return abbr;
}

